import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import i18n from '../../i18n';
import ScreenLoader from '../ScreenLoader';
import { isPortalUser } from '../../utils/rbac';
import { resolveRouteAccess, ACCESS } from '../../utils/routeAccess';
import useDeploymentScope from '../../hooks/useDeploymentScope';

// <Route exact path='/claims'>
//   <RequireRole resource='claims' action='read'>
//     <ClaimsScreen />
//   </RequireRole>
// </Route>
//
// Three redirects, in order of precedence:
//   1. No auth at all      → /login (was /dashboard, which looped back
//                            through this same guard — the source of D-35
//                            where logged-out URLs sat on /dashboard etc.
//                            instead of bouncing to login).
//   2. Portal user hitting → /portal/<home> (portal roles pass RBAC on
//      a back-office route    claims.read / policies.read etc. because the
//                             backend also gates via /me/* portalRoutes;
//                             the shared resource list means a broker or
//                             policyholder could reach /clients or
//                             /commissions with the correct URL. D-31/34.)
//   3. Auth but insufficient→ /dashboard + toast (unchanged).
//      RBAC
//
// This is UI polish + defense-in-depth; the backend still 403s on the
// request itself.
const PORTAL_HOMES = {
  policyholder:       '/portal/my-policies',
  broker:             '/portal/my-clients',
  claimantPortalUser: '/portal/my-claims',
};

// Regional deployments (e.g. ch.getregure.com) run without the platform-
// admin surface. SaaS God (role='admin') on those deploys should only
// reach Account Settings — every other back-office / platform URL either
// hits a physically-absent backend route or shows tenant data admin has
// no reason to see. hasPermission short-circuits `admin` → true, so
// without this per-route gate operators could type any URL and load it.
// Sidebar already enforces the same restriction (see
// ADMIN_VISIBLE_ITEMS_REGIONAL in utils/rbac.js). D-01, D-02, V-03, V-05.
// Paths a SaaS God can reach on a regional deploy. Must mirror
// utils/rbac.js ADMIN_VISIBLE_ITEMS_REGIONAL — otherwise the sidebar
// shows an item that the route guard bounces on click. /platform/tenants
// is always mounted on regional deploys (backend/router/v1.js line 146
// mounts it BEFORE the REGURE_DEPLOYMENT_SCOPE gate) because per-tenant
// management is needed on the deploy that hosts the tenant.
const ADMIN_REGIONAL_ALLOWED_PATHS = new Set(['/account-settings', '/platform/tenants']);
// Same story for :orgId detail pages — regex match on TenantDetail path.
const ADMIN_REGIONAL_ALLOWED_PATTERNS = [
  /^\/platform\/tenants\/[a-f0-9]{24}$/i,
];

const RequireRole = ({ resource, action, children, redirectTo = '/dashboard' }) => {
  // Read from BOTH Redux slices — `loggedinUser` (populated by
  // getLoggedinUserAction) AND `userLogin` (populated by classic login).
  // Prior to 2026-07-15 only `userLogin` was consulted, so any user
  // hydrated exclusively via getLoggedinUserAction had role === undefined
  // here → the `if (!role) return children` fall-open branch let them
  // see gated screens. LogTable already reads both slices for the same
  // reason (see comment on LogTable.js:12).
  const role = useSelector((state) => {
    const loggedIn = state?.loggedinUser?.userInfo?.data;
    const info     = state?.userLogin?.userInfo?.data;
    return loggedIn?.data?.role || info?.data?.role || info?.user?.role || null;
  });
  // While the store hydrates, we prefer a stable client-side cache to
  // avoid a flash of unauthorized content on hard refresh. Falls back
  // to whatever localStorage happens to hold — deliberately last resort,
  // and deliberately NOT enough to accuse anyone of anything: see
  // resolveRouteAccess.
  const fallbackRole = (typeof localStorage !== 'undefined' && localStorage.userInfo)
    ? (() => { try { return JSON.parse(localStorage.userInfo)?.data?.user?.role || null; } catch { return null; } })()
    : null;
  // A /users/get-user request in flight means the session is still
  // resolving. Holding through that window is what stops the guard
  // reporting a denial that the resolved session then contradicts.
  const sessionLoading = useSelector((state) => Boolean(state?.loggedinUser?.loading));

  const deployScope = useDeploymentScope();
  const location = useLocation();

  const access = resolveRouteAccess({
    reduxRole: role,
    cachedRole: fallbackRole,
    sessionLoading,
    resource,
    action,
  });
  const effectiveRole = access.role;
  const allowed = access.allowed;

  useEffect(() => {
    if (access.toast) {
      toast.error(i18n.t('common.errors.noPermission', {
        defaultValue: 'You do not have permission to access this page.',
      }));
    }
  }, [access.toast]);

  // (0) Session still resolving and no identity to work from — hold
  //     rather than bounce a user with a live cookie out to /login.
  if (access.decision === ACCESS.WAIT) return <ScreenLoader />;

  // (1) Not logged in — send to /login. Previously the default
  //     `redirectTo='/dashboard'` looped back through this same guard on
  //     every back-office route, so an unauthenticated URL hit just sat
  //     blank instead of bouncing (D-35).
  if (access.decision === ACCESS.LOGIN) return <Redirect to='/login' />;

  // (1a) SaaS God on a regional deploy — only Account Settings is
  //      allowed. Wait until deployScope resolves so we don't flash the
  //      target screen on hard refresh and then bounce; a null scope
  //      means /health hasn't answered yet. All other roles are
  //      unaffected — this branch only fires for role='admin'.
  if (
    effectiveRole === 'admin' &&
    deployScope &&
    deployScope !== 'platform-admin' &&
    !ADMIN_REGIONAL_ALLOWED_PATHS.has(location.pathname) &&
    !ADMIN_REGIONAL_ALLOWED_PATTERNS.some(re => re.test(location.pathname))
  ) {
    return <Redirect to='/account-settings' />;
  }

  // (2) Portal role landing on a back-office route — send to portal home.
  //     Back-office resources (claims, policies, etc.) are shared with
  //     portal roles for the /me/* endpoints; without this guard a portal
  //     user could type a back-office URL and mount screens they should
  //     never see (D-31, D-34).
  if (isPortalUser(effectiveRole)) {
    return <Redirect to={PORTAL_HOMES[effectiveRole] || '/portal/my-profile'} />;
  }

  // (3) Logged in but role isn't on the allow-list — bounce back to the
  //     dashboard with a toast.
  if (!allowed) return <Redirect to={redirectTo} />;
  return <>{children}</>;
};

export default RequireRole;
